import { expVal } from '@atlassian/jira-feature-experiments';
import type { IssueId } from '@atlassian/jira-software-board-common/src/index.tsx';
import type { Issue } from '../../../../model/issue/issue-types.tsx';
import flagMessages from '../../../../view/flags/messages.tsx';
import { warningFlagMeta } from '../../meta/flag-meta.tsx';
import {
	beginOptimisticUiMeta,
	commitOptimisticUiMeta,
	revertOptimisticUiMeta,
	revertOptimisticUiWithFlagMeta,
	type OptimisticUiMeta,
} from '../../meta/optimistic-ui-meta.tsx';

export const ISSUE_DELETE_REQUEST = 'state.actions.issue.delete.ISSUE_DELETE_REQUEST' as const;

export type IssueDeleteRequestAction = {
	type: typeof ISSUE_DELETE_REQUEST;
	payload: {
		issue: Issue;
		childIssueIds: IssueId[];
	};
	meta: OptimisticUiMeta;
};

export const issueDeleteRequest = (
	issue: Issue,
	childIssueIds: IssueId[],
): IssueDeleteRequestAction => ({
	type: ISSUE_DELETE_REQUEST,
	payload: {
		issue,
		childIssueIds,
	},
	meta: {
		...beginOptimisticUiMeta(),
	},
});

export const ISSUE_DELETE_SUCCESS = 'state.actions.issue.delete.ISSUE_DELETE_SUCCESS' as const;

export type IssueDeleteSuccessAction = {
	type: typeof ISSUE_DELETE_SUCCESS;
	payload: {
		issueId: IssueId;
	};
	meta: OptimisticUiMeta;
};

export const issueDeleteSuccess = (
	optimisticId: string,
	issueId: IssueId,
): IssueDeleteSuccessAction => ({
	type: ISSUE_DELETE_SUCCESS,
	payload: {
		issueId,
	},
	meta: commitOptimisticUiMeta(optimisticId),
});

export const ISSUE_DELETE_FAILURE = 'state.actions.issue.delete.ISSUE_DELETE_FAILURE' as const;

export type IssueDeleteFailureAction = {
	type: typeof ISSUE_DELETE_FAILURE;
	payload: {
		issueId: IssueId;
	};
	meta: OptimisticUiMeta;
};

export const issueDeleteFailure = (
	optimisticId: string,
	issue: Issue,
	errorMessages: string[] | null = null,
): IssueDeleteFailureAction => {
	const titleMessage = (() => {
		// display issueType details only when it is present
		if (issue.typeName && expVal('jira-itr-m4-exp-3', 'isEnabled', false)) {
			return flagMessages.deleteFailureTitleIssueTerminologyRefreshM4;
		}

		return expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? flagMessages.deleteFailureTitleIssueTermRefresh
			: flagMessages.deleteFailureTitle;
	})();

	return {
		type: ISSUE_DELETE_FAILURE,
		payload: {
			issueId: issue.id,
		},
		meta: errorMessages
			? {
					...revertOptimisticUiMeta(optimisticId),
					...warningFlagMeta({
						id: 'ISSUE_DELETE_ERROR_FLAG',
						titleMessage,
						descriptionMessage: flagMessages.genericDescription,
						rawDescriptionMessage: errorMessages.join('\n\n'),
						...(issue.typeName && expVal('jira-itr-m4-exp-3', 'isEnabled', false)
							? { context: { workType: issue.typeName } }
							: {}),
					}),
				}
			: revertOptimisticUiWithFlagMeta(optimisticId),
	};
};

export type Action = IssueDeleteRequestAction | IssueDeleteSuccessAction | IssueDeleteFailureAction;
